export const LANGUAGES = ['zh-Hans', 'zh-Hant', 'en']
export const EVENT_CONTENT_FIELDS = ['title', 'subtitle', 'eventType', 'location', 'description']
export const CATEGORY_CONTENT_FIELDS = ['categoryName', 'description']
export const STAGE_CONTENT_FIELDS = ['stageName']
export const ROUND_CONTENT_FIELDS = ['roundName', 'description']
export const fieldName = (key, locale) => key + (locale === 'en' ? 'En' : locale === 'zh-Hant' ? 'ZhHant' : '')
const clone = value => JSON.parse(JSON.stringify(value))

export function createContentDraft(record, fields, review = false) {
  const draft = clone(record)
  draft.sourceLocaleHint = (draft.translationMeta && draft.translationMeta.sourceHint) || 'auto'
  draft.originalContent = {}
  fields.forEach(key => {
    const previous = draft.translationMeta && draft.translationMeta.fields && draft.translationMeta.fields[key]
    // An older client may have edited the base field without updating provenance.
    const externalEdit = previous && previous.values && (draft[key] || '') !== (previous.values['zh-Hans'] || '')
    draft.originalContent[key] = previous && !externalEdit ? previous.sourceText : (draft[key] || '')
    if (review && previous && previous.heldValues) {
      LANGUAGES.forEach(locale => { draft[fieldName(key, locale)] = previous.heldValues[locale] || '' })
      previous.values = clone(previous.heldValues)
      delete previous.heldValues
    }
  })
  return draft
}

export function pendingContent(draft, fields, force = false) {
  const texts = {}
  fields.forEach(key => {
    const text = draft.originalContent[key] || ''
    const previous = draft.translationMeta && draft.translationMeta.fields && draft.translationMeta.fields[key]
    if (force || !previous || previous.needsTranslation || previous.sourceText !== text || previous.sourceHint !== draft.sourceLocaleHint) texts[key] = text
  })
  return texts
}

export function mergeContentTranslation(draft, fields, generated) {
  const result = clone(draft)
  const metadata = { version: 1, sourceHint: draft.sourceLocaleHint, fields: {} }
  fields.forEach(key => {
    const previous = draft.translationMeta && draft.translationMeta.fields && draft.translationMeta.fields[key]
    const incoming = generated[key] || previous
    if (!incoming) throw new Error('翻译结果不完整，原文未更改')
    const sourceText = draft.originalContent[key] || ''
    const changed = previous ? previous.sourceText !== sourceText : sourceText !== (draft[key] || '')
    const entry = {
      ...clone(incoming), sourceText, sourceHint: draft.sourceLocaleHint,
      values: {}, manualLocales: [], reviewLocales: [],
      generatedValues: clone(incoming.generatedValues || incoming.values)
    }
    if (generated[key]) { delete entry.heldValues; entry.needsTranslation = false }
    if (!sourceText) entry.warnings = []
    LANGUAGES.forEach(locale => {
      const protectedValue = previous && previous.heldValues && (previous.manualLocales || []).includes(locale)
      const current = protectedValue ? previous.heldValues[locale] : (draft[fieldName(key, locale)] || '')
      const priorValue = previous && previous.values[locale]
      const wasManual = previous && (previous.manualLocales || []).includes(locale)
      const edited = previous ? current !== (priorValue || '') : !!current && locale !== 'zh-Hans'
      let translated = incoming.values[locale] || ''
      if (!sourceText) translated = '' // Clearing an original also clears stale translations.
      else if (locale === incoming.sourceLocale) translated = sourceText
      else if (wasManual || edited) {
        translated = current
        entry.manualLocales.push(locale)
        if (changed || (previous && (previous.reviewLocales || []).includes(locale))) entry.reviewLocales.push(locale)
      }
      result[fieldName(key, locale)] = translated
      entry.values[locale] = translated
    })
    metadata.fields[key] = entry
  })
  result.translationMeta = metadata
  return result
}

export function contentNeedsReview(draft) {
  return Object.values((draft.translationMeta && draft.translationMeta.fields) || {}).some(field =>
    field.needsTranslation || (field.reviewLocales || []).length || (field.warnings || []).length)
}

// A normal form can save its original even when inference fails. Unverified text
// never masquerades as a translation; the public API exposes a pending notice.
export function deferContentTranslation(draft, fields, failedTexts = {}) {
  const result = clone(draft)
  const meta = result.translationMeta || { version: 1, sourceHint: draft.sourceLocaleHint, fields: {} }
  fields.forEach(key => {
    const failed = Object.prototype.hasOwnProperty.call(failedTexts, key)
    let entry = meta.fields[key]
    const flagged = entry && (entry.needsTranslation || (entry.warnings || []).length || (entry.reviewLocales || []).length)
    if (!failed && !flagged) return
    const text = result.originalContent[key] || ''
    const sourceChanged = (entry ? entry.sourceText : (draft[key] || '')) !== text
    if (failed) entry = {
      ...(entry || {}), sourceText: text, sourceHint: draft.sourceLocaleHint,
      sourceLocale: draft.sourceLocaleHint === 'auto' ? (/\p{Script=Han}/u.test(text) ? 'zh-Hans' : 'en') : draft.sourceLocaleHint,
      needsTranslation: true, warnings: ['translation_pending'], values: (entry && entry.values) || {},
      manualLocales: (entry && entry.manualLocales) || LANGUAGES.filter(locale => locale !== 'zh-Hans' && draft[fieldName(key, locale)])
    }
    if (failed && sourceChanged) entry.reviewLocales = [...new Set([...(entry.reviewLocales || []), ...entry.manualLocales])]
    entry.heldValues = entry.heldValues || Object.fromEntries(LANGUAGES.map(locale => [locale, result[fieldName(key, locale)] || '']))
    entry.values = Object.fromEntries(LANGUAGES.map(locale => [locale, text]))
    LANGUAGES.forEach(locale => { result[fieldName(key, locale)] = text })
    meta.fields[key] = entry
  })
  meta.pending = Object.values(meta.fields).some(entry => entry.needsTranslation || (entry.warnings || []).length || (entry.reviewLocales || []).length)
  result.translationMeta = meta
  return result
}

export function confirmContentReview(draft) {
  Object.entries(draft.translationMeta.fields).forEach(([key, field]) => {
    field.reviewLocales = []
    field.warnings = []
    // Explicit editorial approval protects even an unchanged machine suggestion.
    // Failed inference still needs translation; do not approve original-language placeholders.
    if (!field.needsTranslation) field.manualLocales = [...new Set([...(field.manualLocales || []),
      ...LANGUAGES.filter(locale => locale !== field.sourceLocale && draft[fieldName(key, locale)])])]
  })
  draft.translationMeta.pending = contentNeedsReview(draft)
}

export function contentPayload(draft) {
  const payload = clone(draft)
  delete payload.originalContent
  delete payload.sourceLocaleHint
  return payload
}

// Keep SQL column boundaries explicit. Never truncate a translation silently.
export function validateContentLengths(draft, fields, kind) {
  const limits = kind === 'round' ? { roundName: [50, 50, 80], description: [255, 255, 320] }
    : kind === 'stage' ? { stageName: [50, 50, 80] } : kind === 'category'
    ? { categoryName: [100, 100, 150], description: [255, 255, 320] }
    : { title: [150, 150, 200], subtitle: [255, 255, 320], eventType: [50, 80, 100], location: [255, 255, 320], description: [3000, 3000, 3000] }
  fields.forEach(field => LANGUAGES.forEach((locale, index) => {
    if ([...(draft[fieldName(field, locale)] || '')].length > limits[field][index]) {
      throw new Error(`${field} (${locale}) 超过 ${limits[field][index]} 字符，请缩短原文或在系统管理 → 语言管理中调整译文 / Shorten the source or adjust it in Languages`)
    }
  }))
}
